import React from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { estimatedTotalPrice } from '../../util/booking';
import { types as sdkTypes } from '../../util/sdkLoader';
import { useConfiguration } from '../../context/configurationContext';

import css from './OrderBreakdown.module.css';
import classNames from 'classnames';

const { Money } = sdkTypes;

/**
 * Displays the final total price for the customer in the order breakdown.
 *
 * The component calculates the total price using customer-related line items
 * and formats the result using the provided internationalization object.
 *
 * If the transaction is refunded, the displayed total becomes 0.
 * Nothing is rendered when there are no valid customer line items.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Array<Object>} props.txLineItems - Transaction line items used for total calculation.
 * @param {string} props.currency - Currency code used for price calculations.
 * @param {boolean} [props.refunded=false] - Indicates whether the transaction was refunded.
 * @param {React.ReactNode} [props.asterisks] - Optional content appended next to the total label.
 *
 * @returns {JSX.Element|null} Rendered total price section or null.
 */
const LineItemFullPrice = props => {
  const { txLineItems, currency, refunded, asterisks } = props;

  const config = useConfiguration();
  const intl = useIntl();

  if (!txLineItems?.length) {
    return null;
  }

  const totalLabel = <FormattedMessage id="OrderBreakdown.total" />;

  const customerLineItems = txLineItems.filter(item => item.includeFor.includes('customer'));

  if (!customerLineItems.length) return null;

  const totalPrice = refunded
    ? new Money(0, config.currency)
    : estimatedTotalPrice(customerLineItems, currency);

  return (
    <div className={css.lineItemTotal}>
      <span className={classNames(css.totalLabel, css.bold)}>
        {totalLabel}
        {asterisks}
      </span>
      <span className={css.totalPrice}>{formatMoney(intl, totalPrice)}</span>
    </div>
  );
};

export default LineItemFullPrice;
